import React from "react";
import {
  Box, IconButton, Paper, Typography, alpha,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChecklistIcon from "@mui/icons-material/Checklist";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useGetSystemChecklistQuery } from "../../../Api/attoDeskApi";
import { useAppSelector } from "../../../Store/hooks";
import { selectIsSuperAdmin } from "../../../Store/Auth/AuthSelector";
import PageLoader from "../../../Components/Loader/PageLoader";
import {
  NAVY, BORDER, TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../../theme/tokens";

interface IItem {
  id: string;
  label: string;
  pass: boolean;
  detail: string | null;
}

const ChecklistPage: React.FC = () => {
  const isSuper = useAppSelector(selectIsSuperAdmin);
  const { data, isLoading, refetch, isFetching } = useGetSystemChecklistQuery(undefined, {
    skip: !isSuper,
  });

  if (!isSuper) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY }}>
          Production checklist is visible to Super Admins only.
        </Typography>
      </Box>
    );
  }
  if (isLoading) return <PageLoader height="20vh" />;

  const items: IItem[] = Array.isArray(data?.data) ? (data?.data as unknown as IItem[]) : [];
  const meta = (data as any)?.meta || { total: 0, passing: 0, failing: 0 };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: `linear-gradient(135deg, ${alpha("#0e7c9b", 0.18)} 0%, ${alpha("#0e7c9b", 0.06)} 100%)`,
          color: "#0e7c9b",
        }}>
          <ChecklistIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography fontSize={20} fontWeight={800} color={NAVY} letterSpacing="-0.02em">
            Production Checklist
          </Typography>
          <Typography fontSize={12} color={TEXT_SECONDARY}>
            {meta.passing} of {meta.total} checks passing
            {meta.failing > 0 ? ` · ${meta.failing} failing` : ""}
            {isFetching ? " · refreshing…" : ""}
          </Typography>
        </Box>
        <IconButton onClick={() => refetch()}
          sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, width: 38, height: 38, bgcolor: "#fff" }}>
          <RefreshIcon sx={{ fontSize: 18, color: NAVY }} />
        </IconButton>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff" }}>
        {items.map((item) => (
          <Box key={item.id} sx={{
            display: "flex", alignItems: "flex-start", gap: 2, p: 2.5,
            borderBottom: `1px solid ${BORDER}`,
            "&:last-child": { borderBottom: "none" },
            bgcolor: item.pass ? "transparent" : alpha("#fef2f2", 0.4),
          }}>
            <Box sx={{ mt: 0.25 }}>
              {item.pass ? (
                <CheckCircleIcon sx={{ color: "#10b981", fontSize: 20 }} />
              ) : (
                <ErrorOutlineIcon sx={{ color: "#dc2626", fontSize: 20 }} />
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY }}>
                {item.label}
              </Typography>
              {item.detail && (
                <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY,
                  fontFamily: "monospace", mt: 0.5, wordBreak: "break-all" }}>
                  {item.detail}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Paper>
    </Box>
  );
};

export default ChecklistPage;
